var ROM = require("./rom");
var Controller = require("./controller");
var CPU = require("./cpu");
var PPU = require("./ppu");
var PAPU = require("./papu");
var VRC6Sound = require("./papu").VRC6Sound;
var Namco163Sound = require("./papu").Namco163Sound;
var MMC5Sound = require("./papu").MMC5Sound;

var NES = function (opts) {
  this.nes = this;

  this.controllers = {
    1: new Controller(),
    2: new Controller(),
  };

  this.opts = {
    onFrame: function () {},
    onAudioSample: null,
    onStatusUpdate: function () {},
    onBatteryRamWrite: function () {},

    // FIXME: not actually used except for in PAPU
    preferredFrameRate: 60,

    emulateSound: true,
    sampleRate: 48000, // Sound sample rate in hz

    // Draw PAL-length frames: 312 scanlines instead of 262, with the fifty extra
    // lines all in vertical blank. Games written for a European console budget
    // their vblank work against the longer blank, and a few of them -- Asterix
    // (E) is one -- visibly overrun the NTSC one and corrupt the top of the
    // picture. `pal: true` forces it and `autoPal: false` disables the header
    // detection; otherwise loadROM picks it for PAL carts, so Asterix just
    // works without the player finding an option first.
    pal: false,
    autoPal: true,

    // Emulate Nintendo's "Port test controller" (NES-INP-01): the two-plug jig
    // the factory Port Test cartridge expects on both controller ports, which
    // drives a fixed signature on D3/D4 that a bare console leaves undriven.
    // Off by default -- it is test equipment, and with it unplugged the ports
    // read exactly like a console with an ordinary pad (or nothing) in them.
    // Set it before loadROM, or flip it and call reset().
    portTestController: false,

    // The Famicom Disk System's 8 KB BIOS (disksys.rom), as a Uint8Array,
    // ArrayBuffer or plain array. Mapper 20 needs it at $E000-$FFFF and it is
    // the only copyrighted part of an FDS setup, so it is supplied by the user:
    // the page stores it in localStorage and also tries disksys.rom next to the
    // page. Set it before loadROM.
    fdsBios: null,

    // FDS drive status the BIOS checks before a disk call: a write-protected
    // disk makes saving fail with error 3 instead of writing to the image.
    fdsWriteProtect: false,

    // Pull the disk out (false) to see the BIOS's "insert disk" loop.
    fdsDiskInserted: true,
  };
  if (typeof opts !== "undefined") {
    var key;
    for (key in this.opts) {
      if (typeof opts[key] !== "undefined") {
        this.opts[key] = opts[key];
      }
    }
  }

  this.frameTime = 1000 / this.opts.preferredFrameRate;

  // Whether the player asked for PAL explicitly. The automatic detection below
  // must not be able to latch: loading a PAL cart and then an NTSC one has to
  // give each of them its own frame length.
  this._palRequested = !!this.opts.pal;

  this.ui = {
    writeFrame: this.opts.onFrame,
    updateStatus: this.opts.onStatusUpdate,
  };

  // Components are built after opts, because PAPU.reset() reads the sample rate
  // and the frame rate. The PPU has to exist before the CPU, which clocks it.
  this.ppu = new PPU(this);
  this.cpu = new CPU(this);
  this.papu = new PAPU(this);

  this.mmap = null; // set in loadROM()
  this.rom = null;
  this.romData = null;

  // Cartridge sound expansion, registered by the mapper when it needs one.
  this.externalSound = null;

  this.ui.updateStatus("Ready to load a ROM.");

  this.frame = this.frame.bind(this);
  this.buttonDown = this.buttonDown.bind(this);
  this.buttonUp = this.buttonUp.bind(this);
  this.zapperMove = this.zapperMove.bind(this);
  this.zapperFireDown = this.zapperFireDown.bind(this);
  this.zapperFireUp = this.zapperFireUp.bind(this);
};

NES.prototype = {
  fpsFrameCount: 0,
  romData: null,
  break: false,

  // Set break to true to stop frame loop.
  stop: function () {
    this.break = true;
  },

  // Resets the system
  reset: function () {
    // The device resets come first and the cartridge comes last, and the order is
    // load-bearing. cpu.reset() replaces `cpu.mem` with a fresh zero-filled array
    // and ppu.reset() replaces `vramMem` the same way, so everything a mapper
    // copied into those arrays is gone the moment those two return. Calling
    // mmap.reset() before them wiped the ROM windows and never put them back:
    // after a soft reset every read of $8000 returned $00 on every board in the
    // library, and the CPU fetched its reset vector out of zeroes.
    //
    // mmap.loadROM() rather than mmap.reset() because the mapper's own reset only
    // clears its registers; loadROM is what copies the banks in, and it is the
    // only thing in the emulator that knows which bank belongs at $8000 for this
    // cartridge. It calls reset() itself, so the register state comes back too,
    // and its loadBatteryRam() re-reads the saved data rather than clearing it.
    this.cpu.reset();
    this.ppu.reset();
    this.papu.reset();

    if (this.mmap !== null) {
      this.mmap.loadROM();
    }

    // The header mirroring is reapplied on every reset, so a mapper that only
    // changes mirroring at run time is not left pointing at the wrong tables.
    // A mapper that drives the name tables itself is left alone -- it has just
    // run its own update inside loadROM, and overriding it here would be exactly
    // the clobber that its ownsMirroring flag exists to prevent.
    if (
      this.rom !== null &&
      this.rom.valid &&
      !(this.mmap && this.mmap.ownsMirroring)
    ) {
      this.ppu.setMirroring(this.rom.getMirroringType());
    }

    this.lastFpsTime = null;
    this.fpsFrameCount = 0;

    this.break = false;
  },

  frame: function () {
    this.ppu.startFrame();
    var cycles = 0;
    var cpu = this.cpu;
    var ppu = this.ppu;
    var papu = this.papu;
    var mmap = this.mmap;

    FRAMELOOP: for (;;) {
      if (this.break) break;
      // CPU cycles for this burst, before it gets scaled to PPU dots below. The
      // FME-7's IRQ counter runs on CPU cycles, so it needs the real figure.
      var cpuCycles;
      if (cpu.cyclesToHalt === 0) {
        // Execute a CPU instruction
        cpuCycles = cpu.emulate();
        // The APU frame counter, length counters and frame IRQ run whether or
        // not audio samples are generated: `emulateSound` only controls the
        // audio callback, not the APU state machine ($4015 reads, IRQs).
        papu.clockFrameCounter(cpuCycles);
        cycles = cpuCycles * 3;
      } else {
        if (cpu.cyclesToHalt > 8) {
          cpuCycles = 8;
          cycles = 24;
          papu.clockFrameCounter(8);
          cpu.cyclesToHalt -= 8;
        } else {
          cpuCycles = cpu.cyclesToHalt;
          cycles = cpu.cyclesToHalt * 3;
          papu.clockFrameCounter(cpu.cyclesToHalt);
          cpu.cyclesToHalt = 0;
        }
      }

      this.clockMapperCpuCycles(cpuCycles);

      for (; cycles > 0; cycles--) {
        if (
          ppu.curX === ppu.spr0HitX &&
          ppu.f_spVisibility === 1 &&
          ppu.scanline - ppu.firstRenderScanline === ppu.spr0HitY
        ) {
          // Set sprite 0 hit flag:
          ppu.setStatusFlag(ppu.STATUS_SPRITE0HIT, true);
        }

        if (ppu.requestEndFrame) {
          ppu.nmiCounter--;
          if (ppu.nmiCounter === 0) {
            ppu.requestEndFrame = false;
            ppu.startVBlank();
            break FRAMELOOP;
          }
        }

        ppu.curX++;

        // A12, le bit 12 du bus d'adresse de la PPU, change de niveau selon la
        // phase de la ligne : bas pendant la lecture des motifs du fond, haut
        // pendant celle des sprites. C'est ce signal que le MMC3, le MMC4 et le
        // MMC2 filtrent pour dater leur compteur d'IRQ, donc il se met a jour
        // ici, une fois par dot. Sans cela le compteur ne recevait rien de la
        // frame et l'IRQ de decoupe d'ecran de SMB3 n'arrivait jamais.
        ppu.serviceA12();

        if (ppu.curX === 341) {
          ppu.curX = 0;
          ppu.endScanline();

          // The MMC5 and friends compare their counter against a target
          // scanline, so they are clocked once per finished line.
          if (mmap !== null && mmap.irqClockRate !== "a12") {
            mmap.clockIrqCounter();
          }
        }
      }
    }
    this.fpsFrameCount++;
  },

  // The FME-7 counts CPU cycles rather than PPU scans, so its counter has to be
  // told how much time went by after each CPU burst. Driven from the scanline rate
  // instead, it would fire its IRQ up to three times too late, and Batman's status
  // bar and the mid-frame banks its gameplay reads would all land in the wrong
  // place.
  clockMapperCpuCycles: function (cycles) {
    var mmap = this.mmap;
    if (mmap !== null && mmap.watchesCpuCycles === true) {
      mmap.clockCpuCycles(cycles);
    }
  },

  buttonDown: function (controller, button) {
    this.controllers[controller].buttonDown(button);
  },

  buttonUp: function (controller, button) {
    this.controllers[controller].buttonUp(button);
  },

  zapperMove: function (x, y) {
    if (!this.mmap) return;
    this.mmap.zapperX = x;
    this.mmap.zapperY = y;
  },

  zapperFireDown: function () {
    if (!this.mmap) return;
    this.mmap.zapperFired = true;
  },

  zapperFireUp: function () {
    if (!this.mmap) return;
    this.mmap.zapperFired = false;
  },

  getFPS: function () {
    var now = +new Date();
    var fps = null;
    if (this.lastFpsTime) {
      fps = this.fpsFrameCount / ((now - this.lastFpsTime) / 1000);
    }
    this.fpsFrameCount = 0;
    this.lastFpsTime = now;
    return fps;
  },

  reloadROM: function () {
    if (this.romData !== null) {
      this.loadROM(this.romData);
    }
  },

  // Loads a ROM file into the CPU and PPU.
  // The ROM file is validated first.
  loadROM: function (data) {
    try {
      // 1. Parsing of the cartridge header.
      var rom = new ROM(this);
      rom.load(data);
      this.rom = rom;

      // 2. Mapper instantiation, before any reset so the mapper sees the
      //    parsed header.
      var mapper = rom.createMapper();
      this.mmap = mapper;

      // 3. Wiring the mapper into the components. The mapper reaches the CPU,
      //    PPU and APU through nes.mmap, so no setter is needed here.
      this.cpu.nes = this;
      this.ppu.nes = this;
      this.papu.nes = this;

      // 4. Sound expansion module, for the boards that carry one.
      //
      // The modules of the previous cartridge have to go first: they are
      // registered in the APU, and the Namco 163's module reads the chip's
      // RAM through the mapper. Once another cartridge is loaded, that mapper
      // is gone, and the leftover module would throw on the next frame --
      // which showed up as "the next game does not start" right after a
      // Namco 163 game.
      this.papu.clearExternalSounds();
      this.externalSound = null;
      if ([24, 26].indexOf(rom.mapperType) !== -1) {
        this.externalSound = new VRC6Sound(this);
        this.papu.addExternalSound(this.externalSound);
      }
      if (rom.mapperType === 19) {
        // The Namco 163's sound lives in the chip's own RAM, which the mapper
        // owns, so the module is handed to the mapper as well.
        this.externalSound = new Namco163Sound(this);
        this.papu.addExternalSound(this.externalSound);
        mapper.sound = this.externalSound;
      }
      if (rom.mapperType === 5) {
        // MMC5: two pulse channels plus the PCM channel at $5000-$5015. The
        // mapper hands $5015 read-back and stays the IRQ owner, so it gets
        // the module as well.
        this.externalSound = new MMC5Sound(this);
        this.papu.addExternalSound(this.externalSound);
        mapper.sound = this.externalSound;
      }

      // 5. Video system. The header (and, for the few carts whose old headers
      // lie about it, the known-PAL table) says whether the game was written
      // for a PAL console. The frame geometry has to be right before the
      // first reset, because ppu.reset() is what reads it.
      this.opts.pal =
        this._palRequested || (this.opts.autoPal !== false && rom.isPal);
      this.frameTime =
        1000 / (this.opts.pal ? 50 : this.opts.preferredFrameRate);

      // 6. Reset, then power-on bank layout.
      this.reset();
      mapper.loadROM();

      // 7. Header mirroring, unless the mapper already selected one at power on.
      if (!mapper.ownsMirroring) {
        this.ppu.setMirroring(rom.getMirroringType());
      }
      this.romData = data;

      if (typeof this.opts.onStatusUpdate === "function") {
        this.opts.onStatusUpdate(
          rom.isFds
            ? "FDS - " +
                rom.fdsSides.length +
                (rom.fdsSides.length > 1 ? " disk sides" : " disk side")
            : rom.getMapperName() +
                " - " +
                (rom.vromCount === 0
                  ? "CHR-RAM"
                  : rom.vromCount * 4 + " KB CHR") +
                (this.opts.pal ? " - PAL" : ""),
        );
      }
    } catch (e) {
      if (typeof this.opts.onStatusUpdate === "function") {
        this.opts.onStatusUpdate("Erreur ROM: " + e.message);
      }
      throw e;
    }
  },

  setFramerate: function (rate) {
    this.opts.preferredFrameRate = rate;
    this.frameTime = 1000 / rate;
    this.papu.setSampleRate(this.opts.sampleRate, false);
  },

  toJSON: function () {
    return {
      // romData: this.romData,
      cpu: this.cpu.toJSON(),
      mmap: this.mmap.toJSON(),
      ppu: this.ppu.toJSON(),
      papu: this.papu.toJSON(),
    };
  },

  fromJSON: function (s) {
    // The device resets are enough here. reset() would also run mmap.loadROM(),
    // which re-maps every bank and asks the CPU for its reset vector -- correct
    // for a reset the player pressed, wrong for a save-state load, where the
    // saved mmap/ppu state is applied on the next line and must not be
    // overwritten by a power-on pass in between.
    this.cpu.reset();
    this.ppu.reset();
    this.papu.reset();

    // this.romData = s.romData;
    this.cpu.fromJSON(s.cpu);
    this.mmap.fromJSON(s.mmap);
    this.ppu.fromJSON(s.ppu);
    this.papu.fromJSON(s.papu);
  },
};

module.exports = NES;
module.exports.NES = NES;

// Export for Webpack and browser usage.
if (typeof window !== "undefined") {
  window.jsnes = window.jsnes || {};
  window.jsnes.NES = NES;
}
